import { signalStore, withState, withMethods, patchState, withComputed, getState } from '@ngrx/signals';
import {
  EligibilityStatementRES,
  EndorsementViewModel,
  PolicyChangeQuoteViewModel,
  PolicyChangeTypeEnum,
  PolicyEndorsementsService,
  ProgramEnum,
  QuoteBreakdownViewModel,
  RCIPWizardViewModel,
  SubmissionStatusEnum,
  SubmissionTypeEnum,
  WizardClassViewModel,
} from '@program-suite/data-common';
import { toDateOnly } from '@program-suite/util-helpers';
import { FormGroup } from '@angular/forms';
import { computed, effect, inject } from '@angular/core';
import { WizardService } from './wizard.service';
import { AuthStore } from '@program-suite/data-auth';

// Define the state interface
interface WizardState extends RCIPWizardViewModel {
  mode: 'create' | 'edit';
  submissionGuid: string | null;
  // Only used for coverage adjustment
  policyChangeGuid: string | null;
  // Only used for coverage adjustment
  policyGuid: string | null;
  // Only used for coverage adjustment
  endorsementEffectiveDate: string | null;
  // Only used for coverage adjustment
  performsNewResConstructionDisabled: boolean;
  // Only used for program app when creating new quote
  tenantId: number | null;
  submissionStatus: SubmissionStatusEnum | null;
  quoteBreakdown: any | QuoteBreakdownViewModel | null;
  eligibilityStatements: EligibilityStatementRES | null;
  endorsementList: EndorsementViewModel[];
  scheduleCreditDb: number | null;
  classesDb: WizardClassViewModel[];
  classesInitialized: boolean;
  disabledEndorsements: string[];
  addAppropriateClassCodeError: boolean;
  isLoading: boolean | null;
  patchWizard: boolean;
  // Validation messages returned by the API when rating (e.g. effective date outside the active program period)
  rateErrors: string[];
}

/**
 * Extracts human-readable validation messages from an API error response.
 * Handles the various shapes ASP.NET / FluentValidation can return:
 *  - ValidationProblemDetails:      { errors: { Field: [msg, ...] }, title, ... }  (ModelState BadRequest with [ApiController])
 *  - Plain ModelState dictionary:   { Field: [msg, ...] }                          (BadRequest(ModelState) without the filter)
 *  - FluentValidation failures:     [{ propertyName, errorMessage }, ...]          (the Rate endpoint)
 *  - ProblemDetails:                { title, detail }
 *  - A raw string body.
 */
function extractValidationMessages(error: any): string[] {
  let body = error?.error ?? error;
  if (!body) {
    return [];
  }

  // The body may arrive as a JSON string if content negotiation didn't parse it
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return [body];
    }
  }

  // FluentValidation failure array (Rate endpoint)
  if (Array.isArray(body)) {
    return body.map((e: any) => e?.errorMessage ?? e?.ErrorMessage ?? e).filter((m: any) => typeof m === 'string');
  }

  const messages: string[] = [];
  if (body.errors && typeof body.errors === 'object') {
    // ValidationProblemDetails: { errors: { Field: [msg, ...] } }
    for (const value of Object.values(body.errors)) {
      if (Array.isArray(value)) {
        messages.push(...value.filter((v) => typeof v === 'string'));
      } else if (typeof value === 'string') {
        messages.push(value);
      }
    }
  } else if (typeof body === 'object') {
    // Plain ModelState dictionary: { Field: [msg, ...] } — only array-valued fields are errors
    // (avoids picking up ProblemDetails scalar metadata like title/type/status)
    for (const value of Object.values(body)) {
      if (Array.isArray(value)) {
        messages.push(...value.filter((v) => typeof v === 'string'));
      }
    }
  }
  if (messages.length > 0) {
    return messages;
  }

  // ProblemDetails single-message fallbacks
  if (typeof body.detail === 'string') {
    return [body.detail];
  }
  if (typeof body.title === 'string') {
    return [body.title];
  }

  return [];
}

// Initial state
const initialState: WizardState = {
  mode: 'edit',
  submissionGuid: null,
  // Only used for coverage adjustment
  policyChangeGuid: null,
  // Only used for coverage adjustment
  policyGuid: null,
  // Only used for coverage adjustment
  endorsementEffectiveDate: toDateOnly(new Date()),
  // Only used for coverage adjustment
  performsNewResConstructionDisabled: false,
  // Only used for program app when creating new quote
  tenantId: null,
  submissionStatus: null,
  submissionType: SubmissionTypeEnum.NewBusiness,
  scheduleCredit: 1,
  agencyId: undefined,
  producerId: undefined,
  effectiveDate: toDateOnly(new Date()),
  licenseNumber: null,
  businessType: undefined,
  businessName: null,
  locationAddress1: null,
  locationAddress2: null,
  city: null,
  state: 'CA',
  zipCode: null,
  meetsContinualCoverageFirstThreshold: null,
  performsNewResConstruction: null,
  addAppropriateClassCodeError: false,
  grossReceipts: undefined,
  residentialSubcontractorCosts: undefined,
  commercialSubcontratorCosts: undefined,
  deductible: null,
  occurenceAggregateLimit: null,
  hasEmployees: null,
  numberOfEmployees: null,
  classes: [{ classCode: null, payroll: undefined }],
  endorsements: [], // selected endorsements
  mailingSameAsBusiness: false,
  mailingStreetAddress: null,
  mailingAptSuiteBuilding: null,
  mailingCity: null,
  mailingState: null,
  mailingZipCode: null,
  contactFirstName: null,
  contactLastName: null,
  contactEmail: null,
  contactPhone: null,
  contactCellPhone: null,
  inspectionContactFirstName: null,
  inspectionContactLastName: null,
  inspectionContactEmail: null,
  inspectionContactPhone: null,
  inspectionContactCellPhone: null,
  inspectionContactSameAsPrimary: false,
  descriptionOfOperations: null,
  percentageResidentialNew: 0,
  percentageResidentialRemodel: 0,
  percentageCommercialNew: 0,
  percentageCommercialRemodel: 0,
  percentageIndustrialNew: 0,
  percentageIndustrialRemodel: 0,
  agreeWithEligibilityStatement: true,
  quoteBreakdown: null,
  eligibilityStatements: null,
  endorsementList: [], // a list of endorsements from the lookup API call
  disabledEndorsements: [], // a list of endorsements that are disabled in the UI
  scheduleCreditDb: null,
  classesDb: [{ classCode: null, payroll: undefined }],
  classesInitialized: false,
  isLoading: false,
  patchWizard: false,
  rateErrors: [],
};

export const WizardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    totalPayroll: computed(() => {
      return (state.classes?.() ?? []).reduce((acc, curr) => {
        const payrollStr = String(curr.payroll ?? '0');
        const cleanPayroll = Number(payrollStr.replace(/[^0-9.-]+/g, ''));
        return acc + cleanPayroll;
      }, 0);
    }),
    totalPercentageWork: computed(() => {
      const residentialNew = state.percentageResidentialNew?.() ?? 0;
      const residentialRemodel = state.percentageResidentialRemodel?.() ?? 0;
      const commercialNew = state.percentageCommercialNew?.() ?? 0;
      const commercialRemodel = state.percentageCommercialRemodel?.() ?? 0;
      const industrialNew = state.percentageIndustrialNew?.() ?? 0;
      const industrialRemodel = state.percentageIndustrialRemodel?.() ?? 0;

      return +residentialRemodel + +commercialRemodel + +industrialRemodel + +residentialNew + +commercialNew + +industrialNew;
    }),
    duplicateClasses: computed(() => {
      const classCodes = (state.classes?.() ?? []).map(c => c.classCode);
      const uniqueClassCodes = new Set(classCodes);
      return classCodes.length !== uniqueClassCodes.size;
    }),
  })),
  withMethods((store, wizardService = inject(WizardService), authStore = inject(AuthStore), policyChangeService = inject(PolicyEndorsementsService)) => ({
    loadSpinner(isLoading: boolean) {
      patchState(store, { isLoading });
    },
    getState() {
      return getState(store);
    },
    setSubmissionGuid(guid: string): void {
      patchState(store, { submissionGuid: guid });
    },
    setPolicyChangeGuid(guid: string): void {
      patchState(store, { policyChangeGuid: guid });
    },
    setPolicyGuid(guid: string): void {
      patchState(store, { policyGuid: guid });
    },
    setTenantId(tenantId: number): void {
      patchState(store, { tenantId });
    },
    checkEndorsements(): void {
      const { performsNewResConstruction, classes, submissionType } = getState(store);
      let disabledEndorsements = [...(store.disabledEndorsements?.() ?? [])];
      let updatedEndorsements = [...(store.endorsements?.() ?? [])];
      let addAppropriateClassCodeError = false; // Shows error if class code is not appropriate and disables proceed button
      let newResToggleSelected = false; // Controls if 'NewResLimitationBuyback' endorsement is selected/included or not
      let newResToggleLocked = true; // Controls if the toggle is locked or not

      // Helper to update disabledEndorsements
      const updateDisabledEndorsements = (endorsement: string, add: boolean) => {
        const index = disabledEndorsements.indexOf(endorsement);
        if (add && index === -1) {
          disabledEndorsements.push(endorsement);
        } else if (!add && index !== -1) {
          disabledEndorsements.splice(index, 1);
        }
      };

      // Helper to add/remove endorsement from updatedEndorsements
      const setEndorsement = (endorsement: string, selected: boolean) => {
        const hasEndorsement = updatedEndorsements.includes(endorsement);
        if (selected && !hasEndorsement) {
          updatedEndorsements = [...updatedEndorsements, endorsement];
        } else if (!selected && hasEndorsement) {
          updatedEndorsements = updatedEndorsements.filter((code) => code !== endorsement);
        }
      };

      // Step 1: Check class code scenarios
      const has91340 = classes?.some((c) => c.classCode === '91340'); // 91340 - Carpentry - New Construction
      const has91342 = classes?.some((c) => c.classCode === '91342'); // 91342 - Carpentry - Remodeling
      const classCount = classes?.length || 0;

      // Class code logic takes precednece over performsNewResConstruction
      if (has91340) {
        // Remove exclusion, lock toggle
        newResToggleSelected = true;
        newResToggleLocked = true;
      } else if (has91342 && classCount === 1) {
        // Include exclusion, lock toggle
        newResToggleSelected = false;
        newResToggleLocked = true;
        // Check for error condition
        if (performsNewResConstruction) {
          addAppropriateClassCodeError = true;
        }
      } else {
        // Step 2: Fallback to performsNewResConstruction logic
        if (performsNewResConstruction) {
          // Yes: Remove exclusion, lock toggle
          newResToggleSelected = true;
          newResToggleLocked = true;
        } else {
          // No: Include exclusion, lock toggle
          newResToggleSelected = false;
          newResToggleLocked = true;
        }
      }

      // Step 3: Update endorsement state
      setEndorsement('NewResLimitationBuyback', newResToggleSelected);

      // Step 4: Update disabledEndorsements to disable toggle based on lock state
      updateDisabledEndorsements('NewResLimitationBuyback', newResToggleLocked);

      //Step 5: Disable NoSunset if is renewal or coverage adjustment
      if (submissionType === SubmissionTypeEnum.Renewal || store.policyGuid()) {
        updateDisabledEndorsements('NoSunset', true);
      } else {
        updateDisabledEndorsements('NoSunset', false);
      }

      //Step 6: Disable CoverageSublimit100k if subcontracting costs are 0
      const residentialSubcontractorCosts = store.residentialSubcontractorCosts?.() ?? 0;
      const commercialSubcontratorCosts = store.commercialSubcontratorCosts?.() ?? 0;
      const hasSubcontractingCosts = residentialSubcontractorCosts > 0 || commercialSubcontratorCosts > 0;

      if (!hasSubcontractingCosts) {
        updateDisabledEndorsements('CoverageSublimit100k', true);
        // Remove CoverageSublimit100k from endorsements if it was selected
        setEndorsement('CoverageSublimit100k', false);
      } else {
        updateDisabledEndorsements('CoverageSublimit100k', false);
      }

      // Step 7: Washington state StopGap coverage rules
      const stateCode = store.state?.();
      const hasEmployees = store.hasEmployees?.();

      if (stateCode === 'WA') {
        if (hasEmployees === false) {
          // No employees: StopGap1Million remains available (not disabled)
          updateDisabledEndorsements('StopGap1Million', false);
        } else if (hasEmployees === true) {
          // Has employees: StopGap1Million is optional
          updateDisabledEndorsements('StopGap1Million', false);
        } else {
          // hasEmployees is null/undefined - disable until user answers
          updateDisabledEndorsements('StopGap1Million', true);
        }
      } else {
        // Non-WA states: remove any WA-specific restrictions on StopGap
        updateDisabledEndorsements('StopGap1Million', false);
      }

      // Step 8: Apply state changes
      patchState(store, {
        addAppropriateClassCodeError,
        disabledEndorsements,
        endorsements: updatedEndorsements,
      });
    },
    async updateFields(fields: Partial<RCIPWizardViewModel>): Promise<void> {
      if (!store.classesInitialized()) {
        fields.classes = store.classesDb()?.map((c: WizardClassViewModel) => ({ classCode: c.classCode ?? null, payroll: c.payroll }));
      }
      // Set numberOfEmployees to 0 when hasEmployees is false
      if (fields.hasEmployees === false) {
        fields.numberOfEmployees = 0;
      }
      patchState(store, fields);
      this.checkEndorsements();
    },
    async updateEndorsements(endorsementCode: string, isAdding: boolean, rate: boolean = false, skipCheckEndorsements: boolean = false): Promise<void> {
      this.loadSpinner(true);
      try {
        const currentEndorsements = store.endorsements?.() ?? [];

        let updatedEndorsements;
        if (isAdding) {
          // Only add if it doesn't already exist
          if (!currentEndorsements.includes(endorsementCode)) {
            updatedEndorsements = [...currentEndorsements, endorsementCode];
          } else {
            updatedEndorsements = currentEndorsements; // No change if duplicate
          }
        } else {
          updatedEndorsements = currentEndorsements.filter((code) => code !== endorsementCode);
        }

        patchState(store, {
          endorsements: updatedEndorsements,
        });

        // Re-check endorsement rules after user toggle (but not when called from checkEndorsements itself)
        if (rate && !skipCheckEndorsements) {
          this.checkEndorsements();
        }

        if (rate) {
          await this.rateWizard();
        }
      } catch (error) {
        console.error('Error in updateEndorsements:', error);
        throw error; // Re-throw the error after logging
      } finally {
        this.loadSpinner(false);
      }
    },
    async saveWizard(): Promise<void> {
      this.loadSpinner(true);
      try {
        if (store.policyGuid()) {
          let policyChangeGuid = store.policyChangeGuid();
          var formData = this.createPolicyChangeFormData();
            policyChangeGuid = await policyChangeService.upsertPolicyChangeRequest(formData);
            this.setPolicyChangeGuid(policyChangeGuid);
            var formData = this.createPolicyChangeFormData();
            await wizardService.rateCoverageAdjustmentWizard(formData);
        }
        else if (store.submissionGuid()) {
          await wizardService.updateWizard(store.submissionGuid()!, getState(store));
        } else {
          throw new Error('Missing Submission Guid');
        }
      } catch (error) {
        console.error('Error in saveWizard:', error);
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },
    // if isCoverageAdjustment is true, Id is the PolicyChangeGuid or policyGuid
    // else,                            Id is the SubmissionGuid
    async loadWizard(id: string, isCoverageAdjustment?:boolean): Promise<void> {
      this.loadSpinner(true);
      try {
        var wizardData: RCIPWizardViewModel;
        if (isCoverageAdjustment) {
          wizardData = await wizardService.getCoverageAdjustmentWizard(id);
          const today = new Date();
          const effectiveDateValue = wizardData.effectiveDate ? new Date(wizardData.effectiveDate) : today;
          var endorsementEffectiveDateValue: string | Date = today;
          if ("endorsementEffectiveDate" in wizardData) {
            if (wizardData.endorsementEffectiveDate && typeof wizardData.endorsementEffectiveDate  == 'string') {
              endorsementEffectiveDateValue = new Date(wizardData.endorsementEffectiveDate);
            } 
            wizardData.endorsementEffectiveDate = toDateOnly(endorsementEffectiveDateValue < effectiveDateValue ? effectiveDateValue : endorsementEffectiveDateValue);
          }
        } else {
          wizardData = await wizardService.getWizard(id);
        }
        patchState(store, {
          classesDb: wizardData.classes?.length
            ? wizardData.classes.map((c) => ({ classCode: c.classCode, payroll: c.payroll }))
            : [{ classCode: null, payroll: undefined }],
        });
        const modifiedWizardData = {
          ...wizardData,
          scheduleCreditDb: wizardData.scheduleCredit ?? null,
        };
        patchState(store, modifiedWizardData);
        const user = authStore.user();
        if (user) {
          patchState(store, {
            agencyId: store?.agencyId?.() ?? user.agencyId ?? undefined,
            producerId: store?.producerId?.() ?? user.producerId ?? undefined,
          });
        }
        var quoteBreakdown: QuoteBreakdownViewModel | PolicyChangeQuoteViewModel | null = null;
        if (isCoverageAdjustment && store.policyChangeGuid()) {
          quoteBreakdown = await wizardService.getCoverageAdjustmentQuote(id);
        } 
        if (!isCoverageAdjustment) {
          quoteBreakdown = await wizardService.getQuoteBreakdown(id);
        }
        if (quoteBreakdown) {
          patchState(store, { quoteBreakdown });
        }

        if (!isCoverageAdjustment) {
          const eligibilityStatements = await wizardService.getEligibilityStatements(id);
          if (eligibilityStatements) {
            patchState(store, { eligibilityStatements: eligibilityStatements });
          }
        }

        if (!isCoverageAdjustment) {
          const submissionStatus = await wizardService.getSubmissionStatus(id);
          if (submissionStatus) {
            patchState(store, { submissionStatus });
          }
        }
        this.setPatchWizard(true);
      } catch (error) {
        console.error('Error in loadWizard:', error);
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },

    clearRateErrors(): void {
      patchState(store, { rateErrors: [] });
    },
    // Validates the zip code against the rater's territory data. Returns false only when the
    // rater definitively has no territory for it; fails open on request errors since the rate
    // step re-validates the zip against the same data.
    async validateZip(state: string, zipCode: string, effectiveDate: string | Date): Promise<boolean> {
      try {
        return await wizardService.validateZip(state, zipCode, toDateOnly(effectiveDate));
      } catch (error) {
        console.error('Error in validateZip:', error);
        return true;
      }
    },
    async rateWizard(): Promise<string | null | PolicyChangeQuoteViewModel> {
      this.loadSpinner(true);
      patchState(store, { rateErrors: [] });
      try {
        // Check if the wizard is in coverage adjustment mode
        if (store.policyGuid()) {
          let policyChangeGuid = store.policyChangeGuid();
          let formData = this.createPolicyChangeFormData();
          if (!policyChangeGuid) {
            policyChangeGuid = await policyChangeService.upsertPolicyChangeRequest(formData);
            this.setPolicyChangeGuid(policyChangeGuid);
            formData = this.createPolicyChangeFormData();
          }
          var quoteBreakdown = await wizardService.rateCoverageAdjustmentWizard(formData);
          if (quoteBreakdown) {
            patchState(store, { quoteBreakdown });
          }
          return quoteBreakdown;
        } else {
          let submissionGuid = store.submissionGuid();
          if (!submissionGuid) {
            const tenantId = store.tenantId();
            submissionGuid = await wizardService.saveNewWizard(getState(store), tenantId ?? undefined);
            patchState(store, { submissionGuid });
          } else {
            await wizardService.updateWizard(submissionGuid, getState(store));
          }

          if (submissionGuid) {
            const quoteBreakdown = await wizardService.rateWizard(submissionGuid);
            patchState(store, { quoteBreakdown });
            return submissionGuid;
          } else {
            throw new Error('Missing Submission Guid');
          }
        }
      } catch (error: any) {
        console.error('Error in rateWizard:', error, 'body:', error?.error);
        let messages = extractValidationMessages(error);
        // Ensure a failed request always surfaces something to the user
        if (messages.length === 0 && error?.status >= 400) {
          messages = ['Unable to rate this submission. Please review your entries and try again.'];
        }
        patchState(store, { rateErrors: messages });
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },
    async getEligibilityStatements(id: string): Promise<void> {
      this.loadSpinner(true);
      try {
        const eligibilityStatements = await wizardService.getEligibilityStatements(id);
        patchState(store, { eligibilityStatements: eligibilityStatements });
      } catch (error) {
        console.error('Error in getEligibilityStatements:', error);
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },
    async getEndorsements(programCode: ProgramEnum, stateCode: string, effectiveDate: string): Promise<void> {
      this.loadSpinner(true);
      try {
        const endorsements = await wizardService.getEndorsements(programCode, stateCode, effectiveDate);
        patchState(store, { endorsementList: endorsements });
      } catch (error) {
        console.error('Error in getEndorsements:', error);
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },
    async submit(): Promise<void> {
      this.loadSpinner(true);
      try {
        if (store.policyChangeGuid()) {
          const formData = this.createPolicyChangeFormData();
          await policyChangeService.submitPolicyChangeRequest(formData);
        }
        else if (store.submissionGuid()) {
          const submissionStatus = await wizardService.createSubmission(store.submissionGuid()!);
          patchState(store, { submissionStatus });
        } else {
          throw new Error('Missing Submission Guid');
        }
      } catch (error) {
        console.error('Error in submit:', error);
        throw error;
      } finally {
        this.loadSpinner(false);
      }
    },
    async policyChangeIssue(): Promise<void> {
      const formData = this.createPolicyChangeFormData();
      await policyChangeService.issuePolicyChangeRequest(formData);
    },
    async policyChangeDecline(): Promise<void> {
      const model = {
        policyChangeGuid: store.policyChangeGuid(),
      };
      try {
        await policyChangeService.declinePolicyChangeRequest(model);
      } catch (error) {
        console.error('Error in policyChangeDecline:', error);
        throw error;
      }
    },
    setClassesInitialized(classesInitialized: boolean): void {
      patchState(store, { classesInitialized });
    },
    createPolicyChangeFormData(): FormData {
      const formData = new FormData();
      if (store.policyChangeGuid()) {
        formData.append('PolicyChangeGuid', store.policyChangeGuid()!);
      }
      formData.append('PolicyGuid', store.policyGuid()!);
      formData.append('PolicyChangeType', PolicyChangeTypeEnum.CoverageAdjustment);
      const today = new Date();
      const effectiveDateValue = store?.effectiveDate?.() ? new Date(store.effectiveDate() ?? "") : today;
      const endorsementEffectiveDateValue = store?.endorsementEffectiveDate?.() ? new Date(store.endorsementEffectiveDate() ?? "") : today;
      var formDataObj = {
        ...getState(store),
        effectiveDate: toDateOnly(effectiveDateValue),
        endorsementEffectiveDate: new Date((endorsementEffectiveDateValue < effectiveDateValue ? effectiveDateValue : endorsementEffectiveDateValue).getTime() + 8 * 60 * 60 * 1000),
      }
      
      formData.append('FormJSON', JSON.stringify(formDataObj));
      return formData;
    },
    setPatchWizard(patchWizard: boolean): void {
      patchState(store, { patchWizard });
    },
    reset(): void {
      patchState(store, initialState);
    },
  })),
);
